/**
 * House — die sechs Etagen als sechs Spalten (MergedLanes, design-hig.md ②).
 *
 * Aus dem Haus mit Etagen, Seitenschienen und vertikalem Scrollen wird EIN
 * horizontal scrollbares Grid aus sechs Spalten à 208 px. Was über mehreren
 * Bahnen wirkt, ist kein Kasten am Rand mehr, sondern ein Balken, der genau die
 * Spalten überspannt, in denen er wirkt — dadurch wird `maco-runner` überhaupt
 * erst sichtbar (stand in den Daten, wurde von der hartkodierten Etagenliste
 * nie gerendert).
 *
 * Zwei Dinge, die hier bewusst anders sind als in der Vorgängerversion:
 *
 *  - **Der Ziel-Status überschreibt den Bau-Status nicht mehr.** Form =
 *    Bauzustand (`archModel.buildState`).
 *  - **Details sind ein Dialog, kein Seitenpanel.** Die Route
 *    `/architecture/:componentId` bleibt der Türöffner; der gewählte Knoten
 *    trägt `aria-current="true"`.
 *
 * **Zwei Seiten, EINE Übersicht.** `/architecture` und `/umsetzungsgrad` zeigen
 * dieselbe Landkarte: dieselben sechs Bahnen, dieselben Querschnitts-Balken,
 * dieselbe Kataster-Reihenfolge, denselben Schmalfenster-Fallback, denselben
 * Dialog. Statt 400 Zeilen zu kopieren (die dann auseinanderlaufen), trägt
 * diese Komponente zwei Schalter:
 *
 *  - `basePath` — unter welcher Route die Knoten ihren Dialog öffnen.
 *  - `goals` — ob Ziel-Marker, Ziel-Zeile und Ziel-Chip im Dialog mitlaufen.
 *    Standard ist `false`: die Architekturseite zeigt KEINE Ziele mehr, sie
 *    stören dort. Nur `/umsetzungsgrad` schaltet sie ein.
 *
 * **Schmales Fenster.** Sechs Spalten brauchen 1.378 px Inhaltsbreite
 * (`ARCH_MIN_WIDTH`); die App gibt höchstens 1.232 px her und auf einem
 * 1.440-px-MacBook ~1.100 px. Darunter kippt dieselbe Landkarte um 90°: die
 * Bahnen stehen untereinander, die Knoten einer Bahn füllen die Breite. Nichts
 * an der Grammatik ändert sich — dieselben Formen, dasselbe Bauzustands-
 * Vokabular, derselbe Ziel-Marker, dieselben Knoten. Nur zwei Dinge, die aus
 * der Geometrie kamen, müssen anders getragen werden: die Spannweite der
 * Querschnitts-Balken (jetzt in Worten) und die Kanten (dort abgeschaltet und
 * im Zähler benannt).
 */
import { useCallback, useRef, useState, type CSSProperties } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FlowEdges, FlowNode, LaneHeader, StateGlyph, edgeCountLabel, laneAriaLabel, stateWord } from "@/components/flow";
import { ALL } from "@/types/pipeline";
import { findGoal, STATUS_LABEL } from "@/components/GoalBadge";
import { GoalLine } from "@/components/goals";
import { HOUSE_COMPONENTS } from "./houseData";
import {
  ARCH_EDGES,
  ARCH_SEQUENCE,
  CROSS_EDGES,
  LANE_EDGES,
  buildStateOf,
  spanCoverage,
  spanLabel,
  spanSentence,
  type ArchMerge,
} from "./archModel";
import { DetailPanel } from "./DetailPanel";
import { useArchLayout } from "./useArchLayout";
import "./house.css";

/**
 * Ziel-Marker: die ANWESENHEIT des Strichs sagt „an ein Atlassian-Ziel
 * gebunden", die Farbe verstärkt nur. Kein Mapping → kein Marker.
 */
function goalMarker(id: string): { status: string; title: string } | null {
  const goal = findGoal(`repo:${id}`);
  if (!goal) return null;
  const status = (goal.status as string | null) ?? "pending";
  const title = [
    goal.goal_key ? `Ziel ${goal.goal_key}` : "Ziel folgt",
    STATUS_LABEL[status] ?? status,
    status !== "done" && goal.target_date ? `bis ${goal.target_date}` : null,
  ]
    .filter(Boolean)
    .join(" · ");
  return { status, title };
}

export interface HouseProps {
  /** Route, unter der die Knoten ihren Dialog öffnen. */
  basePath?: string;
  /** Ziel-Marker, Ziel-Zeile und Ziel-Chip im Dialog zeigen. */
  goals?: boolean;
}

export function House({ basePath = "/architecture", goals = false }: HouseProps = {}) {
  const { componentId } = useParams<{ componentId?: string }>();
  const nav = useNavigate();
  const selected = componentId ? HOUSE_COMPONENTS[componentId] : undefined;

  const rootRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLDivElement>(null);
  const layout = useArchLayout(rootRef);
  const narrow = layout === "narrow";
  const [highlight, setHighlight] = useState<string | null>(null);
  const [drawn, setDrawn] = useState({ total: 0, derived: 0 });

  const open = useCallback((id: string) => nav(`${basePath}/${id}`), [nav, basePath]);
  const close = useCallback(() => nav(basePath), [nav, basePath]);
  const onGeometry = useCallback(
    (g: { total: number; derived: number }) =>
      setDrawn((prev) => (prev.total === g.total && prev.derived === g.derived ? prev : { total: g.total, derived: g.derived })),
    [],
  );

  return (
    <div className="arch" data-layout={layout} ref={rootRef}>
      <div className="arch-scroll">
        {/* position: relative ist Voraussetzung für das Kanten-Overlay. */}
        <div className="arch-canvas" ref={canvasRef}>
          {/* Schmal wird nicht gemessen und nicht gezeichnet — siehe Zähler unten. */}
          <FlowEdges
            canvasRef={canvasRef}
            edges={LANE_EDGES}
            enabled={!narrow}
            highlight={highlight}
            onGeometry={onGeometry}
          />

          {/* Die Reihenfolge ist Modell, nicht Komponente (ARCH_SEQUENCE): breit
              platziert sie das Grid ausdrücklich, schmal IST sie die Lesefolge. */}
          <div className="arch-grid">
            {ARCH_SEQUENCE.map((slot) =>
              slot.kind === "merge" ? (
                <MergedNode
                  key={slot.merge.id}
                  merge={slot.merge}
                  selected={selected?.id}
                  goals={goals}
                  onOpen={open}
                  onHighlight={setHighlight}
                />
              ) : (
                /* display: contents (breit) — Kopf und Stapel bleiben eigene
                   Grid-Items der Bahn-Spalte; schmal wird daraus ein Band. */
                <div
                  key={slot.lane.id}
                  className="arch-lane"
                  style={{ "--col": slot.index + 1 } as CSSProperties}
                >
                  <div className="arch-col-head">
                    <LaneHeader lane={slot.lane.head} release={ALL} vocabulary="build" />
                  </div>
                  <div
                    className="arch-stack"
                    role="group"
                    aria-label={laneAriaLabel(slot.lane.head, ALL, "build")}
                  >
                    {slot.lane.members.map((c) => {
                      const goal = goals ? goalMarker(c.id) : null;
                      return (
                        <FlowNode
                          key={c.id}
                          id={c.id}
                          variant="card"
                          state={buildStateOf(c.id)}
                          vocabulary="build"
                          title={c.name}
                          subtitle={c.laneSub ?? c.type}
                          label={`${c.name}, ${c.type}`}
                          current={selected?.id === c.id}
                          onActivate={() => open(c.id)}
                          onHighlight={setHighlight}
                          goalStatus={goal?.status ?? null}
                          goalTitle={goal?.title}
                        >
                          {/* Geschwister des Kopfs, nie darin — der Schlüssel
                              ist ein echter Link (FlowNode-Markup-Regel). */}
                          {goals && <GoalLine refId={`repo:${c.id}`} />}
                        </FlowNode>
                      );
                    })}
                  </div>
                </div>
              ),
            )}
          </div>
        </div>
      </div>

      <p className="arch-edgecount">
        {narrow ? (
          <>
            {`${LANE_EDGES.length} Kanten im schmalen Layout ausgeblendet`}
            <span className="arch-edgecount-aside">
              {` · untereinander stehende Bahnen geben einer Kurve keine Richtung mehr;` +
                ` alle ${ARCH_EDGES.length} Beziehungen stehen im Dialog jedes Knotens`}
            </span>
          </>
        ) : (
          <>
            {edgeCountLabel(drawn)}
            {CROSS_EDGES.length > 0 && (
              <span className="arch-edgecount-aside">
                {` · ${CROSS_EDGES.length} Beziehungen der Querschnitts-Balken nur im Dialog`}
              </span>
            )}
          </>
        )}
      </p>

      <DetailPanel component={selected} basePath={basePath} goals={goals} onClose={close} />
    </div>
  );
}

/**
 * Ein Querschnitts-Balken. Markup-Regel wie bei `FlowNode`: der Knoten ist ein
 * `<div>`, in dem nur der Kopf ein `<button>` ist; alle Zeilen darin sind
 * `<span>`. Der Name wird bewusst NIE gekürzt — die Rolle darf es.
 *
 * Die Spannweite steht dreifach im Markup, weil sie im schmalen Layout ihre
 * geometrische Trägerin verliert: als überspannte Spalten (nur breit), als
 * Schiene mit sechs Feldern und als Satz („Bahn 3–6 · Knowledge bis
 * Deployment", nur schmal) — und in beiden Lagen im `aria-label`. Ein Balken,
 * der schmal wie ein normaler Knoten aussähe, hätte seine Aussage verloren.
 */
function MergedNode({
  merge,
  selected,
  goals,
  onOpen,
  onHighlight,
}: {
  merge: ArchMerge;
  selected?: string;
  goals: boolean;
  onOpen: (id: string) => void;
  onHighlight: (id: string | null) => void;
}) {
  const c = HOUSE_COMPONENTS[merge.id];
  if (!c) return null;
  const state = buildStateOf(merge.id);
  const sentence = spanSentence(merge);
  const goal = goals ? goalMarker(merge.id) : null;

  return (
    <div
      className="arch-merge"
      data-state={state}
      data-node={merge.id}
      style={
        {
          "--from": merge.from,
          "--to": merge.to,
          "--row": merge.row,
        } as CSSProperties
      }
    >
      <button
        type="button"
        className="arch-merge-head"
        data-flow-node={merge.id}
        aria-current={selected === merge.id ? "true" : undefined}
        aria-label={`${c.name}, ${c.type} — überspannt ${sentence}, ${stateWord(state, "build")}`}
        onClick={() => onOpen(merge.id)}
        onMouseEnter={() => onHighlight(merge.id)}
        onMouseLeave={() => onHighlight(null)}
        onFocus={() => onHighlight(merge.id)}
        onBlur={() => onHighlight(null)}
      >
        <StateGlyph state={state} size={16} />
        <span className="arch-merge-name">{c.name}</span>
        <span className="flow-line arch-merge-role">{merge.role}</span>
        {/* Schiene und Satz tragen die Spannweite dort, wo keine Spalten mehr
            überspannt werden; breit blendet house.css beide aus. */}
        <span className="arch-merge-rail" aria-hidden="true">
          {spanCoverage(merge).map((covered, i) => (
            <span key={i} className="arch-merge-pip" data-in={covered ? "" : undefined} />
          ))}
        </span>
        <span className="arch-merge-sentence">{sentence}</span>
        <span className="arch-merge-span">{spanLabel(merge)}</span>
        {goal && <span className="flow-goalbar" data-goal={goal.status} title={goal.title} aria-hidden="true" />}
      </button>
      {/* Dieselbe Ziel-Zeile wie am Bahn-Knoten — außerhalb des Kopf-Buttons. */}
      {goals && <GoalLine refId={`repo:${merge.id}`} />}
    </div>
  );
}
